// Custom Block Library — gate-engine runtime contract.
//
// The gate VALIDATORS are pure logic; the way untrusted code is EXECUTED is injected. This lets the
// same gate logic run with three different runtimes:
//   • node (CI self-test)      — main-thread `new Function` (trusted test code only);
//   • browser (publish review) — a fresh, frozen-prototype Web Worker (untrusted user code);
//   • edge fn (Phase 2 publish)— a true server isolate.
// A validator NEVER decides how to sandbox — it asks the injected runtime to run code and judges output.

import type { CustomBlockSubtype } from '../../types/customBlockLibrary';

export interface GateCandle { open: number; high: number; low: number; close: number; volume: number; timestamp: number; }
export interface PerBarOutput { value: number[]; signals: boolean[]; }
export interface RunError { error: string }
export const isRunError = (x: unknown): x is RunError => !!x && typeof x === 'object' && 'error' in (x as object);

// Methods return Promises so a runtime can be a real worker ISOLATE (untrusted user code at publish
// time) — the gate must NEVER run untrusted code on the main thread / `new Function`. A trusted/test
// runtime may return plain values synchronously; `await` handles both.
export interface BlockGateRuntime {
  /** Run a per-bar block (indicator/filter/analysis/signal_generator/pattern) → { value, signals }. */
  runPerBar(code: string, blockType: CustomBlockSubtype, candles: GateCandle[], params: Record<string, unknown>): Promise<PerBarOutput | RunError> | PerBarOutput | RunError;
  /** Run a sizing block's computeQty(ctx, params, helpers) → a quantity. */
  runQty?(code: string, ctx: Record<string, unknown>, params: Record<string, unknown>): Promise<number | RunError> | number | RunError;
  /** Run a risk_rule's checkBar(ctx, params, state) per bar → actions[] (null = no action). */
  runRiskRuleCheckBar?(code: string, candles: GateCandle[], params: Record<string, unknown>): Promise<Array<{ action: string } | null> | RunError> | Array<{ action: string } | null> | RunError;
  /** Optional: splice the block into a minimal strategy + backtest (compatibility gate). */
  backtest?(code: string, blockType: CustomBlockSubtype, candles: GateCandle[], params: Record<string, unknown>): Promise<{ trades: number; net: number | null } | RunError>;
}

/** Deterministic synthetic candles (sine + drift). `seed` varies the series for fuzzed determinism. */
export function syntheticCandles(n: number, seed = 0): GateCandle[] {
  const out: GateCandle[] = []; const t0 = 1700000000000;
  for (let i = 0; i < n; i++) {
    const c = 100 + 18 * Math.sin((i + seed * 7) / 12) + 5 * Math.sin((i + seed) / 3) + 3 * Math.sin(i / 41);
    out.push({ open: c - 0.1, high: c + 0.6, low: c - 0.6, close: c, volume: 1000 + (i % 50), timestamp: t0 + i * 3600000 });
  }
  return out;
}

/** Parameter names a block's code reads (params.X / params["X"]). Used by the metadata gate. */
export function referencedParams(code: string): string[] {
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  const dot = /\bparams\.(\w+)/g; while ((m = dot.exec(code)) !== null) if (!m[1].startsWith('__')) out.add(m[1]);
  const idx = /\bparams\[\s*['"](\w+)['"]\s*\]/g; while ((m = idx.exec(code)) !== null) if (!m[1].startsWith('__')) out.add(m[1]);
  return [...out];
}

/** Map a subtype to its required entry-point function name. */
export function entryPointFor(blockType: CustomBlockSubtype): string {
  switch (blockType) {
    case 'pattern': return 'detectPattern';
    case 'analysis': case 'filter': return blockType === 'filter' ? 'calculate' : 'analyze';
    case 'qty_calculator': return 'computeQty';
    case 'sl_calculator': return 'computeSl';
    case 'tp_calculator': return 'computeTp';
    case 'risk_rule': return 'checkBar';
    default: return 'calculate'; // indicator | signal_generator
  }
}
